import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem, OutlinedInput,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";

import {
  createInformationCapitalGoal,
  fetchApplicationTypes,
  fetchStrategicIssues,
  fetchInfoCapitalGoalsApp,
} from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { useAuth } from "../../providers/AuthProvider";

const TYPE = "InformationCapitalGoalApp";

export default function SmInfoCapitalGoalAppCreatePage() {
  const { businessDomainId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const { companyId } = useAuth();
  const t = (id) => intl.formatMessage({ id });

  const [successFactorName, setSuccessFactorName] = useState("");
  const [successFactorDescription, setSuccessFactorDescription] = useState("");
  const [selectedAppType, setSelectedAppType] = useState(null);
  const [selectedNames, setSelectedNames] = useState([]);
  const [sourceIds, setSourceIds] = useState([]);
  const [appTypes, setAppTypes] = useState([]);
  const [strategicIssues, setStrategicIssues] = useState([]);
  const [usedIndicatorNames, setUsedIndicatorNames] = useState(new Set());

  // 指標は選択されたアプリタイプの indicators から取得
  const sourceIndicators = selectedAppType?.indicators || [];

  useEffect(() => {
    (async () => {
      try {
        const [atypes, issues, existingSfs] = await Promise.all([
          fetchApplicationTypes(businessDomainId),
          fetchStrategicIssues(businessDomainId).catch(() => []),
          fetchInfoCapitalGoalsApp(businessDomainId).catch(() => []),
        ]);
        setAppTypes(Array.isArray(atypes) ? atypes : []);
        setStrategicIssues(Array.isArray(issues) ? issues : []);
        const used = new Set(
          (Array.isArray(existingSfs) ? existingSfs : [])
            .flatMap((sf) => sf.indicators || [])
            .map((ind) => ind.indicatorName)
            .filter(Boolean)
        );
        setUsedIndicatorNames(used);
      } catch (e) {
        notify.apiError(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDomainId]);

  const handleAppTypeChange = (e) => {
    const type = appTypes.find((at) => at.applicationTypeId === e.target.value);
    setSelectedAppType(type || null);
    setSelectedNames([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sourceIndicators.length > 0 && selectedNames.length === 0) {
      notify.error(t("financialGoal.indicatorRequired"));
      return;
    }
    try {
      const indicators = selectedNames
        .map((name) => sourceIndicators.find((ind) => ind.indicatorName === name))
        .filter(Boolean);
      const sources = sourceIds
        .map((id) => {
          const issue = strategicIssues.find((s) => s.successFactorId === id);
          if (!issue) return null;
          return { successFactorId: issue.successFactorId, successFactorName: issue.successFactorName };
        })
        .filter(Boolean);
      await createInformationCapitalGoal({
        successFactorName,
        successFactorDescription,
        applicationTypeId: selectedAppType?.applicationTypeId || "",
        indicators,
        sources,
        businessDomainId,
        companyId,
        successFactorType: TYPE,
      });
      notify.success(t("message.create.success"));
      navigate(`/design/${businessDomainId}/strategy-map/info-capital-goals-app`, { replace: true });
    } catch (err) {
      notify.apiError(err);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("infoCapitalGoalApp.create.title")}
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              fullWidth required
              label={t("successFactor.name")}
              value={successFactorName}
              onChange={(e) => setSuccessFactorName(e.target.value)}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth multiline rows={3}
              label={t("successFactor.description")}
              value={successFactorDescription}
              onChange={(e) => setSuccessFactorDescription(e.target.value)}
            />
          </Grid>

          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("applicationType.label")}</InputLabel>
              <Select
                value={selectedAppType?.applicationTypeId || ""}
                label={t("applicationType.label")}
                onChange={handleAppTypeChange}
              >
                <MenuItem value=""><em>—</em></MenuItem>
                {appTypes.map((at) => (
                  <MenuItem key={at.applicationTypeId} value={at.applicationTypeId}>
                    {at.applicationTypeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("strategicIssue.list.title")}</InputLabel>
              <Select
                multiple
                value={sourceIds}
                onChange={(e) => setSourceIds(e.target.value)}
                input={<OutlinedInput label={t("strategicIssue.list.title")} />}
              >
                {strategicIssues.map((item) => (
                  <MenuItem key={item.successFactorId} value={item.successFactorId}>
                    {item.successFactorName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("financialGoal.selectIndicatorHint")}</InputLabel>
              <Select
                multiple
                disabled={sourceIndicators.length === 0}
                value={selectedNames}
                onChange={(e) => setSelectedNames(e.target.value)}
                input={<OutlinedInput label={t("financialGoal.selectIndicatorHint")} />}
              >
                {sourceIndicators.map((ind) => (
                  <MenuItem key={ind.indicatorName} value={ind.indicatorName} disabled={usedIndicatorNames.has(ind.indicatorName)}>
                    {ind.indicatorName}（{ind.unit}）{usedIndicatorNames.has(ind.indicatorName) ? " （登録済み）" : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit" disabled={!successFactorName.trim()}>
            {t("common.add")}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(`/design/${businessDomainId}/strategy-map/info-capital-goals-app`)}
          >
            {t("common.cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
